<a (click)="goToKnowledgeDocuments()" class="back-to-link alc-icon-and-name">
  <mat-icon>chevron_left</mat-icon>
  <span translate="parameters/alcmeon-ai/edit-doc/error/back"></span>
</a>

<mat-card>
  <mat-card-title>{{ title }}</mat-card-title>
  <form [formGroup]="form" (ngSubmit)="save()">
    <!-- Knowledge Document Name -->
    <mat-form-field appearance="outline" class="shrink">
      <mat-label translate="parameters/alcmeon-ai/edit-doc/name"></mat-label>
      <input type="text" matInput formControlName="name" />
      <mat-error *ngIf="form.controls.name.errors?.required" translate="common/form_errors/required"></mat-error>
    </mat-form-field>
    <!-- Active -->
    <mat-slide-toggle color="primary" formControlName="isActive">
      <span translate="parameters/alcmeon-ai/edit-doc/active"></span>
    </mat-slide-toggle>
    <!-- Used by AI Agents -->
    <app-select
      *ngIf="gaiaAiAgentsItems"
      appearance="outline"
      class="agents-select"
      [control]="form.get('usedByAiAgentsIds')"
      [items]="gaiaAiAgentsItems"
      [multiple]="true"
      labelKey="parameters/alcmeon-ai/edit-doc/used-by-ai-agents">
    </app-select>

    <!-- type -->
    <mat-button-toggle-group formControlName="sourceType" name="sourceType" aria-label="Source Type">
      <mat-button-toggle
        [value]="sourceTypeEnum.PDF"
        [disabled]="!isCreation && form.get('sourceType').value !== sourceTypeEnum.PDF"
        ><span translate="parameters/alcmeon-ai/edit-doc/type/pdf"></span
      ></mat-button-toggle>
      <mat-button-toggle
        [value]="sourceTypeEnum.WEB"
        [disabled]="!isCreation && form.get('sourceType').value !== sourceTypeEnum.WEB"
        ><span translate="parameters/alcmeon-ai/edit-doc/type/website"></span
      ></mat-button-toggle>
      <mat-button-toggle
        [value]="sourceTypeEnum.TEXT"
        [disabled]="!isCreation && form.get('sourceType').value !== sourceTypeEnum.TEXT"
        ><span translate="parameters/alcmeon-ai/edit-doc/type/text"></span
      ></mat-button-toggle>
    </mat-button-toggle-group>

    <!-- PDF -->
    <div *ngIf="form.get('sourceType').value === sourceTypeEnum.PDF" class="pdf">
      <div *ngIf="isCreation && !file">
        <button mat-raised-button type="button" (click)="clickToLoadPdf()">
          <span translate="parameters/alcmeon-ai/edit-doc/type/pdf/placeholder"></span>
        </button>
        <input #fileInput type="file" accept=".pdf" (change)="loadPdf($event)" hidden />
      </div>

      <div *ngIf="file" class="document document-creation">
        <mat-icon color="primary">attach_file</mat-icon>
        <div class="doc-informations">
          <div>{{ file.name }}</div>
          <div class="smaller-text" [innerHTML]="file?.size | bytesPrefix"></div>
        </div>

        <button type="button" [matTooltip]="isCreation ? deleteFile : restoreFile" (click)="deletePdf()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div
        *ngIf="!isCreation && !file"
        class="document document-modification"
        [matTooltip]="'parameters/alcmeon-ai/edit-doc/type/pdf/tooltip/download' | translate"
        (click)="downloadPdf()">
        <mat-icon color="primary">attach_file</mat-icon>
        <div class="doc-informations">
          <div>{{ knowledgeDoc.fileName }}</div>
          <div class="smaller-text" [innerHTML]="knowledgeDoc.fileSize | bytesPrefix"></div>
          <div class="upload-date">
            <div class="smaller-text" translate="parameters/alcmeon-ai/edit-doc/type/pdf/upload-date"></div>
            <div class="smaller-text">: {{ knowledgeDoc.uploadedAt | dateTimezone: 'short' }}</div>
          </div>
        </div>
      </div>

      <div *ngIf="!isCreation">
        <button
          mat-raised-button
          type="button"
          class="replace-button"
          (click)="clickToLoadPdf()"
          translate="parameters/alcmeon-ai/edit-doc/type/pdf/replace-file"></button>
        <input #fileInput type="file" accept=".pdf" (change)="loadPdf($event)" hidden />
      </div>
    </div>

    <!-- WEB -->
    <div *ngIf="form.get('sourceType').value === sourceTypeEnum.WEB" class="web">
      <!-- url -->
      <mat-form-field appearance="outline" class="shrink web-url">
        <mat-label translate="parameters/alcmeon-ai/edit-doc/type/website/placeholder"></mat-label>
        <input type="text" matInput formControlName="url" />
        <mat-hint translate="parameters/alcmeon-ai/edit-doc/type/website/hint"></mat-hint>
        <mat-error *ngIf="form.controls.url.errors?.required" translate="common/form_errors/required"></mat-error>
        <mat-error *ngIf="form.controls.url.errors?.url" translate="common/form_errors/invalid_url"></mat-error>
        <mat-error
          *ngIf="form.controls.url.errors?.pattern"
          translate="parameters/alcmeon-ai/edit-doc/error/https"></mat-error>
      </mat-form-field>

      <div>
        <!-- updateFrequency -->
        <app-select
          *ngIf="linkDepthItems"
          appearance="outline"
          class="web-select"
          [control]="form.get('updateFrequency')"
          [items]="updateFrequencyItems"
          [labelKey]="'parameters/alcmeon-ai/edit-doc/frequency'"
          [formFieldClass]="'no-vertical-padding'">
        </app-select>

        <!-- linkDepth -->
        <app-select
          *ngIf="linkDepthItems"
          appearance="outline"
          class="web-select"
          [control]="form.get('linkDepth')"
          [items]="linkDepthItems"
          [labelKey]="'parameters/alcmeon-ai/edit-doc/depth'"
          [formFieldClass]="'no-vertical-padding'">
        </app-select>
        <mat-error
          *ngIf="form.controls.linkDepth.errors?.required && form.controls.linkDepth.dirty"
          class="specific-error"
          translate="common/form_errors/required"></mat-error>
      </div>

      <div *ngIf="!isCreation" class="web-buttons">
        <button mat-raised-button type="button" (click)="forceContentRefresh()">
          <span translate="parameters/alcmeon-ai/edit-doc/force-refresh"></span>
        </button>
      </div>
    </div>

    <!-- TEXT -->
    <div *ngIf="form.get('sourceType').value === sourceTypeEnum.TEXT" class="text">
      <!-- text -->
      <mat-form-field appearance="outline" class="shrink">
        <mat-label translate="parameters/alcmeon-ai/edit-doc/type/text/placeholder"></mat-label>
        <textarea
          cdkTextareaAutosize
          cdkAutosizeMinRows="3"
          cdkAutosizeMaxRows="30"
          matInput
          formControlName="text"
          [maxlength]="textInputMaxLimit"
          #text></textarea>
        <mat-hint align="end">{{ text.value.length }} / {{ textInputMaxLimit }}</mat-hint>
        <mat-error *ngIf="form.controls.text.errors?.required" translate="common/form_errors/required">
          <mat-hint class="hint-with-error">{{ text.value.length }} / {{ textInputMaxLimit }}</mat-hint>
        </mat-error>
      </mat-form-field>
    </div>

    <div class="buttons">
      <button mat-raised-button type="submit" color="primary" [disabled]="form.invalid || form.pristine">
        <span translate="save"></span>
      </button>
      <button mat-raised-button type="button" (click)="goToKnowledgeDocuments()">
        <span translate="cancel"></span>
      </button>
    </div>
  </form>

  <app-card-footer *ngIf="!isCreation" [data]="footerContent"></app-card-footer>
</mat-card>
